import { useEffect, useState } from 'react';
import type { Environment } from 'react-relay';
import { graphql, fetchQuery } from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	useIsAutodevEnabledQuery as useIsAutodevEnabledQueryType,
	useIsAutodevEnabledQuery$data,
} from '@atlassian/jira-relay/src/__generated__/useIsAutodevEnabledQuery.graphql';

export const Status = {
	NOT_DETERMINED: 0,
	PROCESSING: 1,
	DETERMINED: 2,
} as const;

type Status = (typeof Status)[keyof typeof Status];

type DevAiUser = {
	hasProductAccess: boolean;
	isAdmin: boolean;
	isRovoEnabled: boolean;
};

export const useIsAutoDevEnabled = (
	atlassianAccountId: string | null,
	cloudId: string,
	relayEnv: Environment,
	isRovoRequired = true,
) => {
	const [isAutoDevEnabled, setIsAutoDevEnabled] = useState(false);
	const [isDetermined, setIsDetermined] = useState<Status>(Status.NOT_DETERMINED);
	useEffect(() => {
		const determineDevAIEnabled = async () => {
			if (atlassianAccountId === null || atlassianAccountId.trim() === '') {
				setIsAutoDevEnabled(false);
				setIsDetermined(Status.DETERMINED);
			} else {
				rovoDevAgentsUserCheck({ atlassianAccountId, cloudId, relayEnv })
					.then((response: DevAiUser | null) => {
						const hasProductAccess = response?.hasProductAccess ?? false;
						const rovoEnabled = response?.isRovoEnabled ?? false;
						setIsDetermined(Status.DETERMINED);
						setIsAutoDevEnabled(hasProductAccess && (isRovoRequired ? rovoEnabled : true));
					})
					.catch(() => {
						setIsAutoDevEnabled(false);
						setIsDetermined(Status.DETERMINED);
					});
			}
		};

		if (isDetermined === Status.NOT_DETERMINED && atlassianAccountId !== '') {
			setIsDetermined(Status.PROCESSING);
			if (fg('coding-agents-limited-beta')) {
				determineDevAIEnabled();
			} else {
				setIsDetermined(Status.DETERMINED);
				setIsAutoDevEnabled(false);
			}
		}
	}, [atlassianAccountId, isDetermined, cloudId, relayEnv, isRovoRequired]);

	return [isAutoDevEnabled, isDetermined] as const;
};

export const rovoDevAgentsUserCheck = async ({
	atlassianAccountId,
	cloudId,
	relayEnv,
}: {
	atlassianAccountId: string;
	cloudId: string;
	relayEnv: Environment;
}): Promise<DevAiUser | null> => {
	const result = await fetchQuery<useIsAutodevEnabledQueryType>(
		relayEnv,

		graphql`
			query useIsAutodevEnabledQuery(
				$atlassianAccountId: ID!
				$cloudId: ID!
				$isAutodevBetaEnabled: Boolean!
			) {
				devai_rovoDevAgentsUser(atlassianAccountId: $atlassianAccountId, cloudId: $cloudId)
					@required(action: THROW)
					@optIn(to: "DevAiUsers")
					@include(if: $isAutodevBetaEnabled) {
					hasProductAccess
					isAdmin
				}

				jira {
					isRovoEnabled(cloudId: $cloudId)
						@optIn(to: "JiraAtlassianIntelligence")
						@include(if: $isAutodevBetaEnabled)
				}
			}
		`,
		{
			atlassianAccountId,
			cloudId,
			isAutodevBetaEnabled: fg('coding-agents-limited-beta'),
		},
		{
			fetchPolicy: 'store-or-network',
		},
	)
		.toPromise()
		.then((response: useIsAutodevEnabledQuery$data | undefined) => {
			return {
				hasProductAccess: response?.devai_rovoDevAgentsUser?.hasProductAccess ?? false,
				isAdmin: response?.devai_rovoDevAgentsUser?.isAdmin ?? false,
				isRovoEnabled: response?.jira?.isRovoEnabled ?? false,
			};
		});
	return result;
};
